import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import { Horizontal, SBHorizontal } from '#src/atoms/layout/flex/index.tsx';
import { FollowButton } from '#src/components/favorites/FollowButton/index.tsx';
import { SubscribersCount } from '#src/components/favorites/SubscribersCount/index.tsx';
import { isType } from '#src/lib/gql/index.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import { NotificationPreference } from './NotificationPreference/index.tsx';
import { PrivacyPolicyDialog } from './PrivacyPolicyDialog/index.tsx';
import type {
  Follower_dataPartner,
  Follower_subscription,
} from './__generated__/index.graphql.ts';

const Root = styled(SBHorizontal).attrs({ gap: 2 })`
  @media ${laptopAndAbove} {
    justify-content: flex-end;
  }
`;

type Props = {
  subscription: Follower_subscription;
  dataPartner?: Follower_dataPartner | null;
  hideCount?: boolean;
};

export const Follower = ({ subscription, dataPartner, hideCount }: Props) => {
  const [showPrivacyPolicyDialog, setShowPrivacyPolicyDialog] =
    useState<boolean>(false);

  /** Update the count locally for instant feedback, even when you are logged out.
   * FollowButton will then execute the mutation when the user log in either after
   * a signin ou a signup.
   */
  const [subscriptionsCount, setSubscriptionsCount] = useState(
    subscription.subscriptionsCount
  );

  const canChooseRarities =
    isType(subscription, 'Player') ||
    isType(subscription, 'NBAPlayer') ||
    isType(subscription, 'BaseballPlayer');

  const onChange = (value: boolean) => {
    if (dataPartner && value) setShowPrivacyPolicyDialog(true);
  };

  return (
    <Root>
      {!hideCount && <SubscribersCount count={subscriptionsCount} />}
      <Horizontal gap={2}>
        {!!canChooseRarities && !!subscription.currentUserSubscription && (
          <NotificationPreference
            subscription={subscription.currentUserSubscription}
          />
        )}
        <FollowButton
          currentUserSubscription={subscription.currentUserSubscription}
          subscribable={subscription}
          onChange={onChange}
          onClick={(value: boolean) => {
            setSubscriptionsCount(c => c + (value ? 1 : -1));
          }}
        />
        {!!dataPartner && !!showPrivacyPolicyDialog && (
          <PrivacyPolicyDialog
            dataPartner={dataPartner}
            onClose={() => setShowPrivacyPolicyDialog(false)}
          />
        )}
      </Horizontal>
    </Root>
  );
};

Follower.fragments = {
  subscription: gql`
    fragment Follower_subscription on WithSubscriptionsInterface {
      slug
      subscriptionsCount
      currentUserSubscription {
        slug
        ...NotificationPreference_subscription
      }
    }
    ${NotificationPreference.fragments.subscription}
  ` as TypedDocumentNode<Follower_subscription>,
  dataPartner: gql`
    fragment Follower_dataPartner on DataPartner {
      slug
      ...PrivacyPolicyDialog_dataPartner
    }
    ${PrivacyPolicyDialog.fragments.dataPartner}
  ` as TypedDocumentNode<Follower_dataPartner>,
};
